import { useContext, useMemo, memo, useCallback, useState, useRef, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AppContext } from './AppContext.tsx';
import type { Tab } from '../types/tab.ts';
import PlusIcon from './icons/plus.svg?react';
import CrossIcon from './icons/cross.svg?react';
import Spinner from './Spinner.jsx';
import { isElectron } from '../utils/platform.js';
import { MAX_TABS } from '../lib/constants.ts';

const spinnerVariants = {
  hidden: { opacity: 0, scale: 0.5 },
  visible: { opacity: 1, scale: 1 },
};

const closeButtonVariants = {
  hidden: { scale: 0.5 },
  visible: { scale: 1 },
};

const iconTransition = {
  duration: 0.15,
  ease: [0.25, 0.46, 0.45, 0.94],
};

// Context menu component
const TabContextMenu = memo(({ x, y, tab, onClose, onCloseOthers, onCloseAll, onRename, onDismiss }) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onDismiss();
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onDismiss]);

  return (
    <div
      ref={menuRef}
      role="menu"
      aria-orientation="vertical"
      aria-label="Tab context menu"
      className="fixed z-50 p-1 min-w-[160px] border border-border-default rounded-md bg-bg-dropdown shadow-lg"
      style={{ left: x, top: y }}
    >
      <button
        role="menuitem"
        className="w-full flex items-center h-7 px-1 text-sm text-primary hover:bg-bg-hover transition-colors duration-200 rounded"
        onClick={() => { onRename(); onDismiss(); }}
      >
        Rename
      </button>
      <div className="my-1 border-t border-border-divider" role="separator" />
      <button
        role="menuitem"
        className="w-full flex items-center h-7 px-1 text-sm text-primary hover:bg-bg-hover transition-colors duration-200 rounded"
        onClick={() => { onClose(); onDismiss(); }}
      >
        Close
      </button>
      <button
        role="menuitem"
        className="w-full flex items-center h-7 px-1 text-sm text-primary hover:bg-bg-hover transition-colors duration-200 rounded"
        onClick={() => { onCloseOthers(); onDismiss(); }}
      >
        Close Others
      </button>
      <div className="my-1 border-t border-border-divider" role="separator" />
      <button
        role="menuitem"
        className="w-full flex items-center h-7 px-1 text-sm text-error hover:bg-bg-hover transition-colors duration-200 rounded"
        onClick={() => { onCloseAll(); onDismiss(); }}
      >
        Close All
      </button>
    </div>
  );
});

TabContextMenu.displayName = 'TabContextMenu';

const TabItem = memo(({ tab, isActive, onSelect, onClose, onContextMenu, onStartRename, isRenaming, isRenameSaving, onFinishRename, style, isDragTarget, onDragStart, onDragOver, onDragEnd, onDrop }) => {
  const [renameValue, setRenameValue] = useState(tab.title || 'New Chat');
  const renameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isRenaming && renameInputRef.current) {
      setRenameValue(tab.title || 'New Chat');
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [isRenaming, tab.title]);

  const handleClick = useCallback((e) => {
    e.stopPropagation();
    if (!isRenaming) onSelect(tab.id);
  }, [tab.id, onSelect, isRenaming]);

  const handleMouseDown = useCallback((e) => {
    if (e.button === 1) {
      e.preventDefault();
      e.stopPropagation();
      onClose(tab.id);
    }
  }, [tab.id, onClose]);

  const handleClose = useCallback((e) => {
    e.stopPropagation();
    onClose(tab.id);
  }, [tab.id, onClose]);

  const handleContextMenu = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    onContextMenu(e, tab);
  }, [tab, onContextMenu]);

  const handleDoubleClick = useCallback((e) => {
    e.stopPropagation();
    onStartRename(tab.id);
  }, [tab.id, onStartRename]);

  const handleRenameSubmit = useCallback(() => {
    const trimmed = renameValue.trim();
    onFinishRename(tab.id, trimmed || 'New Chat');
  }, [tab.id, renameValue, onFinishRename]);

  const handleRenameKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      handleRenameSubmit();
    } else if (e.key === 'Escape') {
      onFinishRename(tab.id, null); // null = cancel
    }
  }, [tab.id, handleRenameSubmit, onFinishRename]);

  const displayTitle = tab.title || 'New Chat';

  return (
    <div
      role="tab"
      aria-selected={isActive}
      aria-label={`Tab: ${displayTitle}`}
      style={style}
      draggable={!isRenaming}
      onDragStart={(e) => onDragStart(e, tab)}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      onDrop={(e) => onDrop(e, tab)}
      onContextMenu={handleContextMenu}
      onDoubleClick={handleDoubleClick}
      className={`
        flex items-center gap-2 px-2 py-1 rounded transition-all duration-200
        cursor-pointer group relative min-w-0
        ${isActive 
          ? 'bg-bg-active text-primary' 
          : 'text-secondary hover:text-primary'
        }
        ${isDragTarget ? 'ring-1 ring-accent' : ''}
      `}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
    >
      {isRenaming ? (
        <input
          ref={renameInputRef}
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          onBlur={handleRenameSubmit}
          onKeyDown={handleRenameKeyDown}
          className="flex-1 min-w-0 text-sm bg-bg-input text-primary px-1 py-0 rounded border border-border-default outline-none focus:border-accent"
          maxLength={60}
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <span className="truncate text-sm flex-1" title={displayTitle}>
          {displayTitle}
        </span>
      )}
      <AnimatePresence initial={false}>
        {(tab.isGenerating || isRenameSaving) && (
          <motion.span
            key="spinner"
            variants={spinnerVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            transition={iconTransition}
            className="flex-shrink-0"
          >
            <Spinner size="sm" color="text-secondary" />
          </motion.span>
        )}
      </AnimatePresence>
      {!isRenaming && (
        <motion.button
          onClick={handleClose}
          variants={closeButtonVariants}
          initial="hidden"
          animate="visible"
          transition={iconTransition}
          className="flex-shrink-0 p-0.5 rounded hover:bg-bg-hover opacity-0 group-hover:opacity-100 transition-opacity"
          aria-label={`Close tab: ${displayTitle}`}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <CrossIcon className="w-3 h-3" />
        </motion.button>
      )}
    </div>
  );
});

TabItem.displayName = 'TabItem';

const TabBar = () => {
  if (!isElectron()) {
    return null;
  }

  const {
    tabs,
    activeTabId,
    switchTab,
    closeTab,
    createTab,
    closeOtherTabs,
    closeAllTabs,
    reorderTabs,
    renameTab,
    conversations,
    favoriteConversations,
  } = useContext(AppContext);

  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; tab: Tab } | null>(null);
  const [renamingTabId, setRenamingTabId] = useState<string | null>(null);
  const [renameSavingTabId, setRenameSavingTabId] = useState<string | null>(null);
  const [dragOverTabId, setDragOverTabId] = useState<string | null>(null);
  const dragSourceRef = useRef<Tab | null>(null);

  const sortedTabs = useMemo(() => {
    return [...tabs].sort((a, b) => a.createdAt - b.createdAt);
  }, [tabs]);

  const tabsWithTitles = useMemo(() => {
    return sortedTabs.map(tab => {
      if (tab.conversationId && !tab.title) {
        const conv = conversations.find(c => c.conversationId === tab.conversationId) ||
                    favoriteConversations.find(c => c.conversationId === tab.conversationId);
        if (conv) {
          return { ...tab, title: conv.title || 'New Chat' };
        }
      }
      return tab;
    });
  }, [sortedTabs, conversations, favoriteConversations]);

  const handleNewTab = useCallback(() => {
    createTab();
  }, [createTab]);

  // Context menu handlers
  const handleContextMenu = useCallback((e: React.MouseEvent, tab: Tab) => {
    setContextMenu({ x: e.clientX, y: e.clientY, tab });
  }, []);

  const dismissContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  // Rename handlers
  const handleStartRename = useCallback((tabId: string) => {
    setRenamingTabId(tabId);
  }, []);

  const handleFinishRename = useCallback(async (tabId: string, newTitle: string | null) => {
    setRenamingTabId(null);
    if (newTitle !== null) {
      setRenameSavingTabId(tabId);
      try {
        await renameTab(tabId, newTitle);
      } finally {
        setRenameSavingTabId(null);
      }
    }
  }, [renameTab]);

  // Drag handlers
  const handleDragStart = useCallback((e: React.DragEvent, tab: Tab) => {
    dragSourceRef.current = tab;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', tab.id);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDragOverTab = useCallback((e: React.DragEvent, tab: Tab) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragSourceRef.current && dragSourceRef.current.id !== tab.id) {
      setDragOverTabId(tab.id);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, targetTab: Tab) => {
    e.preventDefault();
    setDragOverTabId(null);
    const sourceTab = dragSourceRef.current;
    if (!sourceTab || sourceTab.id === targetTab.id) return;

    const fromIndex = sortedTabs.findIndex(t => t.id === sourceTab.id);
    const toIndex = sortedTabs.findIndex(t => t.id === targetTab.id);
    if (fromIndex !== -1 && toIndex !== -1) {
      reorderTabs(fromIndex, toIndex);
    }
    dragSourceRef.current = null;
  }, [sortedTabs, reorderTabs]);

  const handleDragEnd = useCallback(() => {
    dragSourceRef.current = null;
    setDragOverTabId(null);
  }, []);

  const showTabs = tabs.length > 1;
  const tabWidth = `${100 / tabsWithTitles.length}%`;

  return (
    <>
      <div className="flex items-center justify-between gap-1 w-full bg-bg-panel border-b border-border-default p-1">
        <div 
          className="flex flex-1 min-w-0"
          role="tablist"
          onDragOver={handleDragOver}
        >
          {showTabs ? (
            tabsWithTitles.map(tab => (
              <TabItem
                key={tab.id}
                tab={tab}
                isActive={tab.id === activeTabId}
                onSelect={switchTab}
                onClose={closeTab}
                onContextMenu={handleContextMenu}
                onStartRename={handleStartRename}
                isRenaming={renamingTabId === tab.id}
                isRenameSaving={renameSavingTabId === tab.id}
                onFinishRename={handleFinishRename}
                style={{ width: tabWidth }}
                isDragTarget={dragOverTabId === tab.id}
                onDragStart={handleDragStart}
                onDragOver={(e) => handleDragOverTab(e, tab)}
                onDragEnd={handleDragEnd}
                onDrop={handleDrop}
              />
            ))
          ) : (
            <div
              className="flex items-center px-2 py-1 text-sm text-primary truncate min-w-0 flex-1"
              onContextMenu={(e) => {
                if (tabsWithTitles[0]) handleContextMenu(e, tabsWithTitles[0]);
              }}
              onDoubleClick={() => {
                if (tabsWithTitles[0]) handleStartRename(tabsWithTitles[0].id);
              }}
            >
              {renamingTabId === tabsWithTitles[0]?.id ? (
                <input
                  autoFocus
                  defaultValue={tabsWithTitles[0]?.title || 'New Chat'}
                  onBlur={(e) => handleFinishRename(tabsWithTitles[0].id, e.target.value.trim() || 'New Chat')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleFinishRename(tabsWithTitles[0].id, (e.target as HTMLInputElement).value.trim() || 'New Chat');
                    if (e.key === 'Escape') handleFinishRename(tabsWithTitles[0].id, null);
                  }}
                  className="flex-1 min-w-0 text-sm bg-bg-input text-primary px-1 py-0 rounded border border-border-default outline-none focus:border-accent"
                  maxLength={60}
                />
              ) : (
                <span>{tabsWithTitles[0]?.title || 'New Chat'}</span>
              )}
            </div>
          )}
        </div>

        <button
          onClick={handleNewTab}
          className={`flex-shrink-0 p-1.5 rounded-lg text-secondary hover:text-primary hover:bg-bg-hover transition-colors ${tabs.length >= MAX_TABS ? 'opacity-50 cursor-not-allowed' : ''}`}
          aria-label="New tab"
          disabled={tabs.length >= MAX_TABS}
        >
          <PlusIcon className="w-4 h-4" />
        </button>
      </div>

      {/* Context menu portal */}
      {contextMenu && (
        <TabContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          tab={contextMenu.tab}
          onClose={() => closeTab(contextMenu.tab.id)}
          onCloseOthers={() => closeOtherTabs(contextMenu.tab.id)}
          onCloseAll={() => closeAllTabs()}
          onRename={() => handleStartRename(contextMenu.tab.id)}
          onDismiss={dismissContextMenu}
        />
      )}
    </>
  );
};

TabBar.displayName = 'TabBar';

export default TabBar;
